import * as React from "react";
import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";

import { cn } from "@/lib/utils";

export type CardProps = useRender.ComponentProps<"div"> & {
  variant?: "default" | "purple" | "muted";
  size?: "default" | "sm";
};

function CardComponent(
  {
    className,
    size = "default",
    variant = "default",
    render,
    ...props
  }: CardProps,
  ref: React.Ref<HTMLDivElement>
) {
  return useRender({
    defaultTagName: "div",
    ref,
    props: mergeProps<"div">(
      {
        className: cn(
          "group/card flex flex-col gap-(--card-spacing) data-[size=sm]:gap-1.5 overflow-hidden rounded-xl  py-(--card-spacing) text-sm text-card-foreground [--card-spacing:--spacing(6)] has-[>img:first-child]:pt-0 data-[size=sm]:[--card-spacing:--spacing(4)] *:[img:first-child]:rounded-t-xl *:[img:last-child]:rounded-b-xl",
          {
            "bg-purple-50 ring-1 ring-purple-800/10": variant === "purple",
            "bg-card shadow-xs ring-1 ring-foreground/10":
              variant === "default",
            "bg-sidebar": variant === "muted",
          },
          className
        ),
      },
      props
    ),
    render,
    state: {
      slot: "card",
      size,
    },
  });
}

const Card = React.forwardRef(CardComponent);

function CardHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        "group/card-header @container/card-header grid auto-rows-min items-start gap-1 rounded-t-xl px-(--card-spacing) has-data-[slot=card-action]:grid-cols-[1fr_auto] has-data-[slot=card-description]:grid-rows-[auto_auto] [.border-b]:pb-(--card-spacing)",
        className
      )}
      {...props}
    />
  );
}

function CardTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-title"
      className={cn(
        "text-base leading-normal font-medium group-data-[size=sm]/card:text-sm",
        className
      )}
      {...props}
    />
  );
}

function CardDescription({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-description"
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    />
  );
}

function CardAction({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-action"
      className={cn(
        "col-start-2 row-span-2 row-start-1 self-start justify-self-end",
        className
      )}
      {...props}
    />
  );
}

function _CardContent(
  { className, ...props }: React.ComponentProps<"div">,
  ref: React.Ref<HTMLDivElement>
) {
  return (
    <div
      ref={ref}
      data-slot="card-content"
      className={cn("flex flex-col gap-3 px-(--card-spacing)", className)}
      {...props}
    />
  );
}

const CardContent = React.forwardRef(_CardContent);

function CardFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-footer"
      className={cn(
        "flex items-center gap-2.5 rounded-b-xl px-(--card-spacing) [.border-t]:pt-(--card-spacing)",
        className
      )}
      {...props}
    />
  );
}

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardAction,
  CardDescription,
  CardContent,
};
